import React, { useState, useRef } from 'react';
import {
  XMarkIcon,
  ArrowUpTrayIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import inventoryApi from '../../../services/inventoryApi';

const ItemBulkUpload = ({ onSuccess, onCancel }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const n = selectedFile.name.toLowerCase();
    if (!n.endsWith('.csv') && !n.endsWith('.xlsx')) {
      toast.error('Please select a CSV or Excel (.xlsx) file');
      return;
    }
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    setFile(selectedFile);
    setUploadResult(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    try {
      setUploading(true);
      const result = await inventoryApi.bulkUploadItems(file);
      const payload = result.data || result;
      setUploadResult(payload);

      const errCount = payload.errors?.length ?? 0;
      const warnCount = payload.warnings?.length ?? 0;
      if (errCount === 0 && warnCount === 0) {
        toast.success(`Successfully uploaded ${payload.successCount} item(s)`);
        setTimeout(() => onSuccess(), 1500);
      } else {
        toast(payload.message || `Uploaded ${payload.successCount} item(s)`, { icon: '⚠️' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload items');
      console.error('Item bulk upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Items');
      sheet.columns = [
        { header: 'name*', key: 'name', width: 30 },
        { header: 'sku*', key: 'sku', width: 18 },
        { header: 'description', key: 'description', width: 36 },
        { header: 'category', key: 'category', width: 24 },
        { header: 'costprice', key: 'costprice', width: 14 }
      ];
      sheet.addRow({
        name: 'Blue Widget',
        sku: 'WID-BLU-001',
        description: 'Standard blue widget',
        category: 'Electronics',
        costprice: 250
      });
      sheet.getRow(1).font = { bold: true };
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), 'item_bulk_upload_template.xlsx');
    } catch (e) {
      console.error(e);
      toast.error('Could not generate the Excel template');
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/30 z-50 flex items-center justify-center p-4">
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white shadow-2xl rounded-lg flex flex-col">
        <div className="flex-shrink-0 bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <ArrowUpTrayIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white">Bulk upload items</h3>
            </div>
            {!uploading && (
              <button
                type="button"
                onClick={onCancel}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {uploading ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <svg className="animate-spin h-12 w-12 text-indigo-600 mb-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-sm font-medium text-gray-900">Uploading and processing your file…</p>
              <p className="text-xs text-gray-500 mt-1 max-w-sm">
                Large files (1000+ rows) can take several minutes — this keeps waiting no matter how long it
                takes. Please don't close this window.
              </p>
            </div>
          ) : !uploadResult ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Instructions
                  </h4>
                  <ul className="text-xs text-blue-800 space-y-1 ml-5 list-disc">
                    <li>Download the <strong>Excel (.xlsx) template</strong>, or save as <strong>CSV UTF-8</strong> if you prefer</li>
                    <li>
                      Only <strong>name</strong> and <strong>sku</strong> are checked critically — a row is skipped only if
                      either is missing, or if the SKU is already used (in the file or the system)
                    </li>
                    <li>
                      <strong>category</strong> is a category <em>name</em>, not an id — an existing category with that name
                      (case-insensitive) is reused; if none exists, a <strong>new category is created automatically</strong>.
                      A blank category falls back to a shared <strong>Uncategorized</strong> category
                    </li>
                    <li>
                      Every item is created as type <strong>Finished Goods</strong>, unit <strong>unit</strong>, and status{' '}
                      <strong>Active</strong> — these aren't file columns
                    </li>
                    <li>
                      <strong>description</strong> and <strong>costprice</strong> are best-effort: an invalid or oversized
                      value is cleared/defaulted (to blank / 0) and reported as a warning — the item is still created
                    </li>
                    <li>Max file size 10 MB (no row-count limit)</li>
                  </ul>
                </div>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="w-full flex items-center justify-center px-4 py-3 border-2 border-indigo-300 border-dashed rounded-lg text-indigo-600 hover:bg-indigo-50 hover:border-indigo-400 transition-all"
                >
                  <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
                    <span className="font-medium">Download Excel template (.xlsx)</span>
                </button>
              </div>

              <div className="space-y-4">
                <div
                  className={`relative border-2 border-dashed rounded-lg p-6 transition-all ${
                    dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <div className="text-center">
                    <ArrowUpTrayIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    {file ? (
                      <div className="space-y-3">
                        <div className="flex items-center justify-center space-x-2">
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          <span className="text-sm font-medium text-gray-900">{file.name}</span>
                        </div>
                        <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                        <button
                          type="button"
                          onClick={() => {
                            setFile(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-sm text-indigo-600 hover:text-indigo-800"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 mb-2">Drag and drop your CSV or Excel file here, or</p>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                        >
                          Browse files
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="w-full py-2.5 px-4 rounded-md text-white font-medium bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading…' : 'Upload items'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div
                className={`p-4 rounded-lg border ${
                  uploadResult.errorCount > 0 || uploadResult.warningCount > 0
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-green-50 border-green-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  {uploadResult.errorCount > 0 || uploadResult.warningCount > 0 ? (
                    <ExclamationTriangleIcon className="w-6 h-6 text-amber-600 flex-shrink-0" />
                  ) : (
                    <CheckCircleIcon className="w-6 h-6 text-green-600 flex-shrink-0" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {uploadResult.successCount} item(s) created
                      {uploadResult.errorCount > 0 && ` · ${uploadResult.errorCount} row(s) skipped`}
                      {uploadResult.warningCount > 0 && ` · ${uploadResult.warningCount} field(s) cleared`}
                      {uploadResult.categoriesCreated?.length > 0 && ` · ${uploadResult.categoriesCreated.length} new categor${uploadResult.categoriesCreated.length === 1 ? 'y' : 'ies'}`}
                    </p>
                  </div>
                </div>
              </div>
              {uploadResult.categoriesCreated?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-indigo-700 mb-1">New categories created</p>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Name</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Code</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {uploadResult.categoriesCreated.map((c, idx) => (
                          <tr key={`cat-${idx}`}>
                            <td className="px-3 py-2 text-gray-700">{c.name}</td>
                            <td className="px-3 py-2 text-gray-600">{c.code}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {uploadResult.errors?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-1">Skipped rows (missing/invalid name or sku)</p>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Row</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {uploadResult.errors.map((e, idx) => (
                          <tr key={`err-${e.row}-${idx}`}>
                            <td className="px-3 py-2 text-gray-600">{e.row}</td>
                            <td className="px-3 py-2 text-red-700">{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {uploadResult.warnings?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-amber-700 mb-1">Fields cleared (row was still created)</p>
                  <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Row</th>
                          <th className="px-3 py-2 text-left font-semibold text-gray-700">Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {uploadResult.warnings.map((w, idx) => (
                          <tr key={`warn-${w.row}-${idx}`}>
                            <td className="px-3 py-2 text-gray-600">{w.row}</td>
                            <td className="px-3 py-2 text-amber-700">{w.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setUploadResult(null);
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Upload another file
                </button>
                <button
                  type="button"
                  onClick={onSuccess}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemBulkUpload;
